import React, { useCallback, useEffect, useMemo } from 'react';
import type { ContentContribution, Game, Image } from '@sparted/shared-library/business/types';
import type { LanguageCode, MultilingualLabel } from '@sparted/shared-library/business/types/multilingual';
import { t } from 'i18next';
import type { UseFormGetValues, UseFormReset, UseFormWatch } from 'react-hook-form';
import { DeepPartial } from 'ts-essentials';

import { useAppDispatch } from 'Libs/redux/store';

import { Header, HeaderType, OriginalRow } from 'Components/structural/STable/types';
import { PickerSelectedImage } from 'Components/unit/UMediaPicker/UMediaPicker';
import { redirect } from 'Libs/routing';
import { enqueueBasicAlert } from 'Pages/AlertManager';
import { getCurrentBOLanguageOrDefault, Language } from 'Services/language/languageService';
import type { ContentEntity } from 'Store/entities/content/content.types';
import { duplicateContent } from 'Store/entities/content/content/content.hook';
import {
  archiveContent,
  fetchContentById,
  manageContentTranslationValidation,
  unarchiveContent,
  updateContent,
} from 'Store/entities/content/content/content.thunks';

import { ALERT_DURATION } from '../GameplaysForm/gameplay.constants';
import { formatContentForTranslationInterface, formatTranslationInterfaceForContent } from './contentEdition.utils';

export type BaseContentFormValues = {
  name: MultilingualLabel;
  identifier: string;
  thematic: number;
  image: Image | PickerSelectedImage | null;
  knowMore?: Partial<MultilingualLabel> | null;
};

export type ContentFormValues = BaseContentFormValues & {
  game?: DeepPartial<Game> | null;
};

const UnvalidationErrorMessage = () => {
  return (
    <div>
      <p>{t('contents:edition.unvalidation_fail')}</p>
      <ul>
        <li>{t('contents:edition.unvalidation_fail_first_reason')}</li>
        <li>{t('contents:edition.unvalidation_fail_second_reason')}</li>
      </ul>
    </div>
  );
};

type UseContentFormProps = {
  content: ContentEntity;
  watch: UseFormWatch<ContentFormValues>;
  reset: UseFormReset<ContentFormValues>;
  getValues: UseFormGetValues<ContentFormValues>;
  isMasterAdmin: boolean;
  contentLanguageCode: LanguageCode;
  displayTranslationModal: (value: boolean) => void;
  languagesWithCode: Language[];
};

export const useSubmitContentForm = ({
  content,
  watch,
  reset,
  getValues,
  isMasterAdmin,
  contentLanguageCode,
  displayTranslationModal,
  languagesWithCode,
}: UseContentFormProps) => {
  const dispatch = useAppDispatch();
  const contentLanguageName = languagesWithCode.find((lang) => lang.languageCode === contentLanguageCode)?.languageName;

  useEffect(() => {
    if (!content) return;
    const defaultValues = {
      name: content.name || { [contentLanguageCode]: '' },
      identifier: content.identifier || '',
      thematic: content.thematic.id || 0,
      image: content.coverImage || null,
      knowMore: content.knowMore || { [contentLanguageCode]: '' },
      game: content.game,
    };

    reset({ ...defaultValues });
  }, [content]);

  const onAutomaticTranslation = () => {
    reset(getValues());
    dispatch(fetchContentById({ contentId: content.id }));
  };

  const handleSaveContent = async () => {
    const formValues = watch();
    const { unwrap } = dispatch(
      updateContent({
        contentId: content.id,
        content: {
          ...content,
          name: formValues.name,
          identifier: formValues.identifier,
          thematic: { id: formValues.thematic },
          statuses: {
            ...content.statuses,
            [contentLanguageCode]: 'draft',
          },
          ...(formValues.image ? { coverImage: formValues.image } : {}),
          knowMore: formValues.knowMore,
          ...(formValues.game ? { game: formValues.game } : {}),
        },
      }),
    );

    try {
      await unwrap();
      enqueueBasicAlert(
        {
          id: 'content_edition_success',
          status: 'success',
          title: t('contents:edition.update_success'),
        },
        ALERT_DURATION,
      );
    } catch (e: any) {
      if (e.errorCode === 'A-C-UC-007') {
        return enqueueBasicAlert(
          {
            id: 'content_unvalidation_failed',
            icon: 'error',
            status: 'error',
          },
          ALERT_DURATION,
          <UnvalidationErrorMessage />,
        );
      }

      enqueueBasicAlert(
        {
          id: 'content_edition_failed',
          icon: 'error',
          status: 'error',
          title: t('contents:edition.update_fail'),
        },
        ALERT_DURATION,
      );
    }
  };

  const handleContentValidation = useCallback(
    async ({ validate }: { validate: boolean }) => {
      if (isMasterAdmin) {
        const { unwrap } = dispatch(
          manageContentTranslationValidation({
            contentId: content.id,
            languageCode: contentLanguageCode,
            validate,
          }),
        );

        try {
          await unwrap();
          enqueueBasicAlert(
            {
              id: 'content_validation_success',
              status: 'success',
              title: t('contents:edition.validation_success', { language: contentLanguageName }),
            },
            ALERT_DURATION,
          );
        } catch (e) {
          enqueueBasicAlert(
            {
              id: 'content_edition_failed',
              status: 'error',
              icon: 'error',
              title: t('contents:edition.validation_fail', { language: contentLanguageName }),
            },
            ALERT_DURATION,
          );
        }
      }
    },
    [
      isMasterAdmin,
      content,
      contentLanguageCode,
      contentLanguageName,
      dispatch,
      enqueueBasicAlert,
      manageContentTranslationValidation,
    ],
  );

  const handleValidation = useCallback(() => handleContentValidation({ validate: true }), [handleContentValidation]);
  const handleUnValidation = useCallback(() => handleContentValidation({ validate: false }), [handleContentValidation]);

  const onContentAskValidation = useCallback(async () => {
    const { unwrap } = dispatch(
      updateContent({
        contentId: content.id,
        content: {
          ...content,

          statuses: {
            ...content.statuses,
            [contentLanguageCode]: 'waiting-validation',
          },
        },
      }),
    );

    try {
      await unwrap();
      enqueueBasicAlert(
        {
          id: 'content_validation_success',
          status: 'success',
          title: t('contents:edition.validation_success', { language: contentLanguageName }),
        },
        ALERT_DURATION,
      );
    } catch (e) {
      enqueueBasicAlert(
        {
          id: 'content_edition_failed',
          status: 'error',
          icon: 'error',
          title: t('contents:edition.validation_fail', { language: contentLanguageName }),
        },
        ALERT_DURATION,
      );
    }
  }, [content]);

  const onUpdateValidation = async () => {
    const formValues = watch();
    const { unwrap } = dispatch(
      updateContent({
        contentId: content.id,
        content: {
          ...content,
          name: formValues.name,
          identifier: formValues.identifier,
          thematic: { id: formValues.thematic },
          statuses: {
            ...content.statuses,
            [contentLanguageCode]: 'validated',
          },
          ...(formValues.image ? { coverImage: formValues.image } : {}),
          knowMore: formValues.knowMore,
          ...(formValues.game ? { game: formValues.game } : {}),
        },
      }),
    );

    try {
      await unwrap();
      enqueueBasicAlert(
        {
          id: 'content_edition_success',
          status: 'success',
          title: t('contents:edition.update_success'),
        },
        ALERT_DURATION,
      );
    } catch (e) {
      reset();
      enqueueBasicAlert(
        {
          id: 'content_edition_failed',
          status: 'error',
          icon: 'error',
          title: t('contents:edition.update_fail'),
        },
        ALERT_DURATION,
      );
    }
  };

  const onAskTranslation = async (ids: number[]) => {
    const languages = languagesWithCode.filter((lang) => ids.includes(lang.id));

    const newLanguages = languages
      .map((lang) => ({ [lang.languageCode]: 'waiting-translation' }))
      .reduce((acc, curr) => {
        return { ...acc, ...curr };
      });

    const { unwrap } = dispatch(
      updateContent({
        contentId: content.id,
        content: {
          ...content,
          statuses: {
            ...content.statuses,
            ...newLanguages,
          },
        },
      }),
    );
    try {
      await unwrap();
      enqueueBasicAlert(
        {
          id: 'content_translation_success',
          status: 'success',
          title: t('contents:edition.ask_translation_success'),
        },
        ALERT_DURATION,
      );
      displayTranslationModal(false);
    } catch (e) {
      enqueueBasicAlert(
        {
          id: 'content_edition_failed',
          status: 'error',
          icon: 'error',
          title: t('contents:edition.ask_translation_fail'),
        },
        ALERT_DURATION,
      );
    }
  };

  const onDeleteLanguage = async (language: LanguageCode) => {
    const flattenedTranslations = formatContentForTranslationInterface(watch());
    const removedLanguage = flattenedTranslations.map((section) => ({
      ...section,
      items: section.items.map((item) => {
        if (typeof item.value === 'object' && item.value !== null) {
          const { [language]: _, ...newValue } = item.value;
          return { ...item, value: newValue };
        }
        return item;
      }),
    }));

    const formattedContent = formatTranslationInterfaceForContent(removedLanguage, content.game?.type || 'mcq');
    const { [language]: _, ...newStatuses } = content.statuses;

    const { unwrap } = dispatch(
      updateContent({
        contentId: content.id,
        content: {
          ...content,
          name: formattedContent.name,
          knowMore: formattedContent.knowMore,
          game: {
            ...content.game,
            ...(formattedContent.game as Omit<DeepPartial<Game>, 'type'>),
          },
          statuses: newStatuses,
        },
      }),
    );
    try {
      await unwrap();
      enqueueBasicAlert(
        {
          id: 'content_translation_success',
          status: 'success',
          text: t('contents:content_details.translation_deleted'),
        },
        ALERT_DURATION,
      );
    } catch (e) {
      enqueueBasicAlert(
        {
          id: 'content_edition_failed',
          status: 'error',
          icon: 'error',
          text: t('contents:content_details.errors.cant_delete_translation'),
        },
        ALERT_DURATION,
      );
    }
  };

  const handleSaveTranslation = async () => {
    const formValues = watch();
    const { unwrap } = dispatch(
      updateContent({
        contentId: content.id,
        content: {
          ...content,
          name: formValues.name,
          identifier: formValues.identifier,
          thematic: { id: formValues.thematic },
          statuses: {
            ...content.statuses,
            [contentLanguageCode]: 'waiting-translation',
          },
          ...(formValues.image ? { coverImage: formValues.image } : {}),
          knowMore: formValues.knowMore,
          ...(formValues.game ? { game: formValues.game } : {}),
        },
      }),
    );

    try {
      await unwrap();
      enqueueBasicAlert(
        {
          id: 'content_edition_success',
          status: 'success',
          title: t('contents:edition.update_success'),
        },
        ALERT_DURATION,
      );
    } catch (e) {
      enqueueBasicAlert(
        {
          id: 'content_edition_failed',
          status: 'error',
          icon: 'error',
          title: t('contents:edition.update_fail'),
        },
        ALERT_DURATION,
      );
    }
  };

  const contributorData: OriginalRow[] = useMemo(
    () =>
      content && content.contributions
        ? content?.contributions.map(({ contributor, type, date }: ContentContribution, index: number) => ({
            id: index,
            callbackOptions: [],
            dataAccessor: `${contributor.lastName || ''} ${contributor.firstName || ''}`.trim(),
            profilePicture: {
              id: `${index}_profilePicture`,
              type: 'UTableCellProfilePicture',
              params: {
                picture: contributor.profilePicture ? contributor.profilePicture : '',
              },
              wrapperStyle: {
                display: 'flex',
                flex: 1,
              },
            },
            name: {
              id: `${index}_lastName`,
              type: 'UTableCellUser',
              params: {
                name: `${contributor.firstName || ''} ${contributor.lastName || ''}`.trim(),
              },
            },
            action: {
              id: `${index}_action`,
              type: 'UTableCellText',
              params: {
                value: type,
              },
            },
            date: {
              id: `${index}_date`,
              type: 'UTableCellText',
              params: {
                // fix this type in ContentContribution in shared-library
                value: (date as any).substring(0, 16).replace('T', ' - '),
              },
            },
          }))
        : [],
    [content],
  );

  return {
    contributorData,
    handleSaveContent,
    handleValidation,
    handleUnValidation,
    onAskTranslation,
    handleSaveTranslation,
    onContentAskValidation,
    onUpdateValidation,
    onAutomaticTranslation,
    onDeleteLanguage,
  };
};

const noop = () => {};

export const getContributorsColumns = (): Header[] => {
  return [
    {
      id: '0',
      type: HeaderType.EmptyHeader,
      width: '50px',
      disableSorting: true,
      accessor: 'profilePicture',
    },
    {
      id: '1',
      type: HeaderType.CellHeader,
      headerParams: {
        text: t('modal_components:content_contributors_modal.contributor'),
        align: 'left',
        onAction: noop,
      },
      width: '200px',
      disableResizing: true,
      disableSorting: true,
      accessor: 'name',
    },
    {
      id: '2',
      type: HeaderType.CellHeader,
      headerParams: {
        text: t('modal_components:content_contributors_modal.action'),
        align: 'left',
        onAction: noop,
      },
      width: '130px',
      disableResizing: true,
      disableSorting: true,
      accessor: 'action',
    },
    {
      id: '3',
      type: HeaderType.CellHeader,
      headerParams: {
        text: t('modal_components:content_contributors_modal.date'),
        align: 'left',
        onAction: noop,
      },
      width: '120px',
      disableResizing: true,
      disableSorting: true,
      accessor: 'date',
    },
  ];
};

export const useContentActions = () => {
  const dispatch = useAppDispatch();

  const handleDuplicateContent = useCallback(
    async (contentId: number) => {
      const { languageCode } = getCurrentBOLanguageOrDefault();
      try {
        const duplicatedContentId = await duplicateContent(contentId, languageCode);
        redirect(`/content/${duplicatedContentId.id}`);
        enqueueBasicAlert(
          {
            id: 'content_edition_success',
            status: 'success',
            title: t('contents:duplication.duplicate_success'),
          },
          ALERT_DURATION,
        );
      } catch (error) {
        enqueueBasicAlert(
          {
            id: 'content_edition_failed',
            status: 'error',
            title: t('contents:duplication.duplicate_fail'),
            icon: 'error',
          },
          ALERT_DURATION,
        );
      }
    },
    [getCurrentBOLanguageOrDefault, enqueueBasicAlert, duplicateContent],
  );

  const handleArchiveContent = useCallback(
    async (contentId: number) => {
      const { unwrap } = dispatch(archiveContent({ contentId }));

      try {
        await unwrap();
        redirect(`/content`);
        enqueueBasicAlert(
          {
            id: 'content_archive_success',
            status: 'success',
            title: t('contents:archive.archive_success'),
          },
          ALERT_DURATION,
        );
      } catch (error: any) {
        if (error.errorCode === 'A-C-CA-002') {
          return enqueueBasicAlert(
            {
              id: 'content_archive_failed',
              status: 'error',
              title: t('contents:archive.archive_fail_used_in_live_campaign'),
              icon: 'error',
            },
            ALERT_DURATION,
          );
        }

        enqueueBasicAlert(
          {
            id: 'content_archive_failed',
            status: 'error',
            title: t('contents:archive.archive_fail'),
            icon: 'error',
          },
          ALERT_DURATION,
        );
      }
    },
    [archiveContent, enqueueBasicAlert, dispatch],
  );

  const handleUnarchiveContent = useCallback(
    async (contentId: number) => {
      const { unwrap } = dispatch(unarchiveContent({ contentId }));

      try {
        await unwrap();
        enqueueBasicAlert(
          {
            id: 'content_unarchive_success',
            status: 'success',
            title: t('contents:unarchive.unarchive_success'),
          },
          ALERT_DURATION,
        );
      } catch (error) {
        enqueueBasicAlert(
          {
            id: 'content_unarchive_failed',
            status: 'error',
            title: t('contents:unarchive.unarchive_fail'),
            icon: 'error',
          },
          ALERT_DURATION,
        );
      }
    },
    [enqueueBasicAlert, unarchiveContent, dispatch],
  );

  return {
    handleDuplicateContent,
    handleArchiveContent,
    handleUnarchiveContent,
  };
};
